import { action, makeObservable, observable } from "mobx";
import jwtDecode from "jwt-decode";
import { Store, StoredObject } from "../common/store";
import * as ResourceAPI from '../../api/sitesee/resource';
import * as UserAPI from '../../api/sitesee/user';
export class AuthStore extends Store {
    identityAuth;
    resourceAuth;
    constructor(provider) {
        super(provider);
        this.resourceAuth = {};
        makeObservable(this, {
            identityAuth: observable,
            resourceAuth: observable,
            setIdentityAuth: action,
            addResourceAuth: action,
        });
    }
    setIdentityAuth(auth) {
        this.identityAuth = auth;
    }
    async getIdentityAuth() {
        if (this.identityAuth) {
            return this.identityAuth;
        }
        // const ar = await this.provider.getAuth();
        // return new Auth(this, ar.token);
    }
    // TODO: ??Move this to user service
    hasIdentityAuth() {
        return !!this.identityAuth;
    }
    addResourceAuth(auth) {
        this.resourceAuth[auth.resource] = auth;
    }
    async getResourceAuth(resource) {
        if (this.resourceAuth[resource] === undefined) {
            if (!this.identityAuth) {
                return undefined;
            }
            const data = await this.provider.resourceProvider.getAuth(resource, this.identityAuth.token);
            this.addResourceAuth(new ResourceTokenAuth(this, data.token));
        }
        return this.resourceAuth[resource];
    }
}
export class Auth extends StoredObject {
    subject;
    token;
    constructor(store, token, subject) {
        super(store);
        this.subject = subject;
        this.setToken(token);
    }
    setToken(token) {
        this.token = token;
    }
}
export function expires(Base, evaluator, converter) {
    class Expires extends Base {
        _expiryEvaluator = evaluator;
        _expiryConverter = converter;
        isExpired() {
            return this._expiryEvaluator(this);
        }
        get expiry() {
            return this._expiryConverter(this);
        }
    }
    return Expires;
}
function evaluateJWTExpiry(auth) {
    const decoded = jwtDecode(auth.token);
    if (!decoded.exp) {
        return false;
    }
    return Date.now() >= decoded.exp * 1000;
}
function convertJWTExpiry(auth) {
    const decoded = jwtDecode(auth.token);
    if (!decoded.exp) {
        return undefined;
    }
    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
}
export function refreshing(Base, refresher) {
    class Refreshing extends Base {
        refresher = refresher;
        timeoutId;
        initialiseRefresh() {
            if (this.timeoutId !== undefined) {
                return;
            }
            let gracePeriod = 120000; // 2 Minutes in ms
            let retryDelay = 30000;
            const timeLeft = this.expiry.getTime() - Date.now();
            if (timeLeft < 0) {
                throw Error(`Unable to refresh before expiry`);
            }
            while (timeLeft < gracePeriod) {
                gracePeriod -= retryDelay;
                if (gracePeriod < 0) {
                    gracePeriod = 0;
                    break;
                }
            }
            const refreshIn = Math.max(0, timeLeft - gracePeriod);
            const refreshTime = Date.now() + refreshIn;
            const refreshDate = new Date(0);
            refreshDate.setUTCMilliseconds(refreshTime);
            this.timeoutId = setTimeout(() => {
                this.timeoutId = undefined;
                this.refresher(this);
                // TODO: Maybe handle errors
                this.initialiseRefresh();
            }, refreshIn);
        }
    }
    return Refreshing;
}
export class JWTAuth extends expires(Auth, evaluateJWTExpiry, convertJWTExpiry) {
    constructor(store, token, subject) {
        const decoded = jwtDecode(token);
        const { sub } = decoded;
        super(store, token, subject || sub);
    }
    setToken(token) {
        const decoded = jwtDecode(token);
        this.subject = decoded.sub || this.subject;
        super.setToken(token);
    }
}
const resourceTokenRequesters = {
    'link': async (auth) => {
        const decoded = jwtDecode(auth.token);
        return ResourceAPI.getLinkToken(auth.store.root.app.apiBasePath, decoded.source.id);
    },
    'user': async (auth) => {
        return ResourceAPI.getUserToken(auth.store.root.app.apiBasePath, auth.resource, auth.store.identityAuth.token, auth.store.root.app.activeGroup.id);
    }
};
async function refreshResourceToken(auth) {
    const decoded = jwtDecode(auth.token);
    const tokenSourceType = decoded.source.type;
    const requester = resourceTokenRequesters[tokenSourceType];
    if (!requester) {
        // Unable to refresh
        throw new Error('Unable to refresh resource token, invalid source type');
    }
    const { data } = await requester(auth);
    // TODO: Handle errors;
    auth.setToken(data.resourceToken);
}
function evaluateSSRTExpiry(auth) {
    const decoded = jwtDecode(auth.token);
    if (!decoded.exp) {
        return false;
    }
    return Date.now() >= decoded.exp;
}
function convertSSRTExpiry(auth) {
    const decoded = jwtDecode(auth.token);
    if (!decoded.exp) {
        return undefined;
    }
    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
}
export class ResourceTokenAuth extends refreshing(expires(Auth, evaluateSSRTExpiry, convertSSRTExpiry), refreshResourceToken) {
    tokenSourceType;
    get sourceType() {
        return this.tokenSourceType;
    }
    tokenScopes;
    get scope() {
        return this.tokenScopes;
    }
    tokenResource;
    get resource() {
        return this.tokenResource;
    }
    constructor(store, token, subject) {
        const decoded = jwtDecode(token);
        const sub = decoded.source.id;
        super(store, token, subject || sub);
        this.tokenSourceType = decoded.source.type;
        this.tokenScopes = decoded.scope;
        this.tokenResource = decoded.rid;
        this.initialiseRefresh();
    }
    setToken(token) {
        const decoded = jwtDecode(token);
        this.subject = decoded.source.id;
        this.tokenSourceType = decoded.source.type;
        this.tokenScopes = decoded.scope;
        this.tokenResource = decoded.rid;
        super.setToken(token);
    }
}
async function refreshIdentityToken(auth) {
    const response = await UserAPI.getUser(auth.store.root.app.apiBasePath, auth.subject, auth.token);
    if (response.token) {
        auth.setToken(response.token);
    }
}
function evaluateSSITExpiry(auth) {
    const decoded = jwtDecode(auth.token);
    if (!decoded.exp) {
        return false;
    }
    return Date.now() >= decoded.exp * 1000;
}
function convertSSITExpiry(auth) {
    const decoded = jwtDecode(auth.token);
    if (!decoded.exp) {
        return undefined;
    }
    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
}
export class IdentityTokenAuth extends refreshing(expires(Auth, evaluateSSITExpiry, convertSSITExpiry), refreshIdentityToken) {
    constructor(store, token, subject) {
        const decoded = jwtDecode(token);
        const { sub } = decoded;
        super(store, token, subject || sub || decoded.oid);
        this.initialiseRefresh();
    }
    setToken(token) {
        const decoded = jwtDecode(token);
        this.subject = decoded.sub || decoded.oid || this.subject;
        super.setToken(token);
    }
}
//# sourceMappingURL=store.js.map